/**
 * Модель времени Kairo.
 *
 * Главное архитектурное решение: события дня описываются парой
 * «локальная дата (строка) + минуты от полуночи», а НЕ UTC-таймстампом.
 *
 * Почему так:
 *  - завтрак в 08:00 остаётся завтраком в 08:00 после перелёта в другой пояс;
 *  - переход на летнее время не сдвигает расписание;
 *  - `'2026-07-28'` — естественный ключ индекса IndexedDB, диапазонные запросы
 *    по строкам работают быстро и без конвертаций;
 *  - арифметика пересечений интервалов сводится к сравнению целых чисел.
 *
 * Таймстампы (`number`, epoch ms) используются только для служебных полей:
 * `createdAt`, `updatedAt`, `syncedAt`.
 */

/** Локальная дата в формате `YYYY-MM-DD`. */
export type IsoDate = string & { readonly __brand?: 'IsoDate' };

/** Минуты от полуночи: 0 = 00:00, 1439 = 23:59. */
export type MinutesOfDay = number;

export const MINUTES_IN_DAY = 1440;
export const MINUTES_IN_HOUR = 60;

/* ─────────────────────────── Даты ─────────────────────────── */

export function toIsoDate(date: Date): IsoDate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function fromIsoDate(iso: IsoDate): Date {
  const [y, m, d] = iso.split('-').map(Number);
  // Конструктор с числами создаёт дату в локальном поясе — в отличие от
  // `new Date('2026-07-28')`, который трактует строку как UTC-полночь.
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/**
 * Момент времени внутри дня — по часам устройства.
 *
 * Время принадлежит дню, о котором речь, а не сегодняшнему: приём за вчера,
 * встреча в календаре, напоминание на завтра. Правило одно на всё приложение;
 * пока оно жило в четырёх копиях, одна из них могла тихо разойтись с другими.
 */
export function momentAt(date: IsoDate, minutes: MinutesOfDay): number {
  const moment = fromIsoDate(date);
  moment.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return moment.getTime();
}

export function today(): IsoDate {
  return toIsoDate(new Date());
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const date = fromIsoDate(iso);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

export function tomorrow(from: IsoDate = today()): IsoDate {
  return addDays(from, 1);
}

/** Разница в днях: `b - a`. Положительная, если `b` позже. */
export function diffDays(a: IsoDate, b: IsoDate): number {
  const msPerDay = 86_400_000;
  const aTime = fromIsoDate(a).setHours(12, 0, 0, 0); // полдень гасит эффект перехода на летнее время
  const bTime = fromIsoDate(b).setHours(12, 0, 0, 0);
  return Math.round((bTime - aTime) / msPerDay);
}

export function isPast(iso: IsoDate, reference: IsoDate = today()): boolean {
  return iso < reference;
}

export function isToday(iso: IsoDate): boolean {
  return iso === today();
}

/** Понедельник недели, содержащей дату (в России неделя начинается с ПН). */
export function startOfWeek(iso: IsoDate): IsoDate {
  const date = fromIsoDate(iso);
  const weekday = (date.getDay() + 6) % 7; // 0 = понедельник
  date.setDate(date.getDate() - weekday);
  return toIsoDate(date);
}

export function startOfMonth(iso: IsoDate): IsoDate {
  return `${iso.slice(0, 7)}-01`;
}

export function endOfMonth(iso: IsoDate): IsoDate {
  const date = fromIsoDate(iso);
  date.setMonth(date.getMonth() + 1, 0);
  return toIsoDate(date);
}

/** Непрерывный диапазон дат включительно. Для календарных сеток и графиков. */
export function dateRange(from: IsoDate, to: IsoDate): IsoDate[] {
  const total = diffDays(from, to);
  if (total < 0) return [];

  // Курсор двигается по одному объекту `Date`, а не пересобирается из строки на
  // каждый шаг: карта привычки за три года — это больше тысячи дней, и раньше
  // каждый стоил разбора строки, создания даты и сборки строки обратно.
  const cursor = fromIsoDate(from);
  const result: IsoDate[] = [];

  for (let i = 0; i <= total; i += 1) {
    result.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}

/** Индекс дня недели: 0 = понедельник … 6 = воскресенье. */
export function weekdayIndex(iso: IsoDate): number {
  return (fromIsoDate(iso).getDay() + 6) % 7;
}

export function isWeekend(iso: IsoDate): boolean {
  return weekdayIndex(iso) >= 5;
}

/* ─────────────────────── Время внутри дня ─────────────────────── */

/** `'08:30'` → 510. Некорректный ввод даёт 0. */
export function parseTime(time: string): MinutesOfDay {
  const [h, m] = time.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return clampMinutes((h ?? 0) * MINUTES_IN_HOUR + (m ?? 0));
}

/** 510 → `'08:30'`. Значения за пределами суток заворачиваются. */
export function formatTime(minutes: MinutesOfDay): string {
  const normalized = ((Math.round(minutes) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const h = Math.floor(normalized / MINUTES_IN_HOUR);
  const m = normalized % MINUTES_IN_HOUR;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Текущее время в минутах от полуночи.
 *
 * Примитива не было, и его писали заново в каждом слое — четыре копии, из
 * которых одна округляла время до пяти минут, а остальные нет. Из-за этого
 * просрочка приёма наступала на разных экранах в разные минуты.
 *
 * `stepMin` округляет вниз: там, где значение участвует в тяжёлом пересчёте,
 * шаг в несколько минут ограничивает частоту обновлений.
 */
export function nowMinutes(stepMin = 1, reference: Date = new Date()): MinutesOfDay {
  const minutes = reference.getHours() * MINUTES_IN_HOUR + reference.getMinutes();
  return stepMin > 1 ? Math.floor(minutes / stepMin) * stepMin : minutes;
}

export function clampMinutes(minutes: number): MinutesOfDay {
  return Math.max(0, Math.min(MINUTES_IN_DAY - 1, Math.round(minutes)));
}

/**
 * Длительность человеческим языком: `90` → `'1 ч 30 мин'`.
 * Используется в карточках задач и бюджете времени.
 */
export function formatDuration(minutes: number, style: 'short' | 'long' = 'short'): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / MINUTES_IN_HOUR);
  const m = total % MINUTES_IN_HOUR;

  if (style === 'long') {
    const parts: string[] = [];
    if (h > 0) parts.push(`${h} ${plural(h, 'час', 'часа', 'часов')}`);
    if (m > 0) parts.push(`${m} ${plural(m, 'минута', 'минуты', 'минут')}`);
    return parts.join(' ') || '0 минут';
  }

  if (h === 0) return `${m} мин`;
  if (m === 0) return `${h} ч`;
  return `${h} ч ${m} мин`;
}

/**
 * Длительность в часах, округлённая до получаса: `676` → `'11,5 ч'`.
 *
 * Используется в бюджете дня. Там точность до минуты не только не нужна, но и
 * вредна: «11 ч 16 мин» читается как измерение, хотя это оценка, и заставляет
 * вчитываться. Получас — шаг, которым люди реально планируют день.
 *
 * Разделитель дробной части — запятая, как принято в русской типографике.
 */
export function formatDurationHours(minutes: number): string {
  const rounded = Math.round(Math.max(0, minutes) / 30) * 30;
  const hours = rounded / MINUTES_IN_HOUR;
  // `toLocaleString` заодно убирает хвост вида «11,0».
  return `${hours.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ч`;
}

/** Русское склонение по числу: 1 час / 2 часа / 5 часов. */
export function plural(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}

/* ───────────────────────── Интервалы ───────────────────────── */

/**
 * Отрезок времени внутри суток.
 * `end` может превышать 1440 — это означает переход через полночь
 * (например, сон 23:00–07:00 → start = 1380, end = 1860).
 */
export interface TimeSpan {
  start: MinutesOfDay;
  end: number;
}

export function spanOf(start: MinutesOfDay, durationMin: number): TimeSpan {
  return { start, end: start + Math.max(0, durationMin) };
}

export function spanDuration(span: TimeSpan): number {
  return Math.max(0, span.end - span.start);
}

/** Пересекаются ли отрезки. Касание границами (10:00–11:00 и 11:00–12:00) — нет. */
export function overlaps(a: TimeSpan, b: TimeSpan): boolean {
  return a.start < b.end && b.start < a.end;
}

/** Величина пересечения в минутах, 0 если не пересекаются. */
export function overlapMinutes(a: TimeSpan, b: TimeSpan): number {
  return Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start));
}

/**
 * Приводит отрезок к суткам: то, что выходит за полночь, становится вторым
 * куском в начале дня. Нужно для отрисовки сна на дневной сетке.
 */
export function splitAtMidnight(span: TimeSpan): TimeSpan[] {
  if (span.end <= MINUTES_IN_DAY) return [span];
  return [
    { start: span.start, end: MINUTES_IN_DAY },
    { start: 0, end: span.end - MINUTES_IN_DAY },
  ];
}

/**
 * Объединяет пересекающиеся и соприкасающиеся отрезки.
 * Основа расчёта занятого времени: без слияния наложенные события
 * посчитались бы дважды и бюджет дня получился бы неверным.
 */
export function mergeSpans(spans: TimeSpan[]): TimeSpan[] {
  if (spans.length === 0) return [];
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const merged: TimeSpan[] = [{ ...sorted[0]! }];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i]!;
    const last = merged[merged.length - 1]!;
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

/** Свободные промежутки внутри `bounds`, не занятые `busy`. */
export function freeSpans(busy: TimeSpan[], bounds: TimeSpan): TimeSpan[] {
  const merged = mergeSpans(busy);
  const gaps: TimeSpan[] = [];
  let cursor = bounds.start;

  for (const span of merged) {
    if (span.end <= bounds.start) continue;
    if (span.start >= bounds.end) break;
    if (span.start > cursor) gaps.push({ start: cursor, end: Math.min(span.start, bounds.end) });
    cursor = Math.max(cursor, span.end);
  }
  if (cursor < bounds.end) gaps.push({ start: cursor, end: bounds.end });

  return gaps.filter((gap) => gap.end > gap.start);
}

/** Суммарная длительность после слияния пересечений. */
export function totalMinutes(spans: TimeSpan[]): number {
  return mergeSpans(spans).reduce((sum, span) => sum + spanDuration(span), 0);
}

/* ─────────────────────── Форматирование ─────────────────────── */

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEKDAYS_FULL = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
];

export function formatDateShort(iso: IsoDate): string {
  const date = fromIsoDate(iso);
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]}`;
}

/**
 * Промежуток дат по-русски: «26 сентября», «с 24 по 26 сентября»,
 * «с 30 сентября по 2 октября». Год не называется — речь о ближних днях.
 */
export function formatDateRange(from: IsoDate, to: IsoDate): string {
  if (from === to) return formatDateShort(from);

  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  const start = sameMonth ? String(fromIsoDate(from).getDate()) : formatDateShort(from);
  return `с ${start} по ${formatDateShort(to)}`;
}

export function formatDateFull(iso: IsoDate): string {
  const date = fromIsoDate(iso);
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]} ${date.getFullYear()}`;
}

export function weekdayShort(iso: IsoDate): string {
  return WEEKDAYS_SHORT[weekdayIndex(iso)] ?? '';
}

export function weekdayFull(iso: IsoDate): string {
  return WEEKDAYS_FULL[weekdayIndex(iso)] ?? '';
}

/** «Сегодня» / «Завтра» / «Вчера», иначе — дата. Для заголовков экранов. */
export function formatRelativeDay(iso: IsoDate, reference: IsoDate = today()): string {
  const delta = diffDays(reference, iso);
  if (delta === 0) return 'Сегодня';
  if (delta === 1) return 'Завтра';
  if (delta === -1) return 'Вчера';
  if (delta === 2) return 'Послезавтра';
  return formatDateShort(iso);
}

/** Человеческая формулировка дедлайна: «просрочено на 2 дня», «через 3 дня». */
export function formatDeadline(iso: IsoDate, reference: IsoDate = today()): string {
  const delta = diffDays(reference, iso);
  if (delta === 0) return 'Сегодня';
  if (delta === 1) return 'Завтра';
  if (delta === -1) return 'Вчера';
  if (delta < 0) {
    const days = Math.abs(delta);
    return `Просрочено на ${days} ${plural(days, 'день', 'дня', 'дней')}`;
  }
  if (delta <= 7) return `Через ${delta} ${plural(delta, 'день', 'дня', 'дней')}`;
  return formatDateShort(iso);
}
